import Apptimize from '@apptimize/apptimize-web-sdk'
import { ReactElement, Suspense, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'

import Loader from 'authorization-module/lib/ui/atoms/Loader'
import {
  FIND_ACCOUNT,
  clientUrls,
  liquidatedClients,
  nonSupportedClients
} from 'const/account/clients'
import config from 'const/config'
import featureFlags from 'const/featureFlags'
import routes from 'const/routes'
import { useAllAccountSummary, useCreditAccounts } from 'const/syfApiQueries'
import { GlobalAppStyles } from 'globalStyles'
import { getLanguageFromStorage } from 'helpers'
import authenticationRedirect, {
  AuthenticationRedirectProps
} from 'helpers/authenticationRedirect'
import getIvaUserMetadata from 'helpers/getIvaUserMetadata'
import 'helpers/iconLibrary'
import redirectToUrl from 'helpers/redirectToUrl'
import setLanguageFromQueryParams from 'helpers/setLanguageFromQueryParams'
import triggerAnalyticsEvent from 'helpers/triggerAnalyticsEvent'
import { IVAChat } from 'iva-react-web-ui'
import updateFeatureFlags from 'services/updateFeatureFlags'
import { ThemeProvider } from 'styled-components'
import getQueryParam from 'syf-js-utilities/formats/getQueryParam'
import isMockData from 'syf-js-utilities/helpers/isMockData'
import { analyticsConfig } from 'syf-js-utilities/helpers/triggerAnalyticsEvent'
import theme from 'theme'
import Header from 'ui/atoms/Header'
import triggerQualtricsOnLoad from 'utils/triggerQualtricsOnLoad'

import { useQuery } from '@tanstack/react-query'
import CreditLimitIncreaseModal from 'App/CreditLimitIncreaseModal'
import handleLogout from 'helpers/handleLogout'
import { initializeOneTrust } from 'helpers/initializeOneTrust'
import isUniversalClient from 'helpers/isUniversalClient'
import redirectToUniversalIDP from 'helpers/redirectToUniversalIDP'
import triggerQualtrics from 'helpers/triggerQualtrics'
import { useAtomValue } from 'jotai'
import getDynamicYieldResponse from 'pages/AccountsDashboard/DynamicYieldGrid/getDynamicYieldResponse'
import { DynamicYieldData } from 'pages/AccountsDashboard/DynamicYieldGrid/typings'
import InactivityModal from 'pages/InactivityModal'
import { selectedAccountAtom } from 'store/atoms'
import ActivityModal from './ActivityModal'
import AddAccountModal from './AddAccountModal'
import AppRoutes from './App.routes'
import ChangePaymentDueDateModal from './ChangePaymentDueDateModal'
import CreditBalanceRefundDrawer from './CreditBalanceRefund'
import CustomCardArtModal from './CustomCardArt'
import DetailsModal from './DetailsModal'
import MakePaymentModal from './MakePaymentModal'
import ManageCardholderModal from './ManageCardholderModal'
import ReplaceYourCardModal from './ReplaceYourCard'
import RewardsModal from './RewardsModal'
import StatementsModal from './StatementsModal'
import { AppWrapper, Footer } from './subcomponents'
import DigitalCollectionsDrawer from './DigitalCollections'
import CardStatusTrackerModal from './CardStatusTrackerModal'
import PaymentSecurityModal from './PaymentSecurity'

const Dashboard = (): ReactElement => {
  const selectedAccount = useAtomValue(selectedAccountAtom)

  const [isApptimizeInitialized, setIsApptimizeInitialized] = useState(false)

  const onApptimizeInitialized = () => {
    Apptimize.track('Apptimize Initialized')
    // Set a flag to indicate Apptimize data is available and content can be displayed
    setIsApptimizeInitialized(true)
  }

  useEffect(() => {
    Apptimize.setOnApptimizeInitializedCallback(onApptimizeInitialized)
    Apptimize.setup(config.APPTIMIZE_APP_KEY, {
      refresh_metadata_on_setup: true
    })
  }, [])

  const [isFeatureFlagsFetched, setIsFeatureFlagsFetched] = useState(false)
  const subject = sessionStorage.getItem('subject')

  // Set Apptimize userId and update window._SFDDL.apptimize when both are ready
  useEffect(() => {
    if (!isApptimizeInitialized || !subject) return
    // Set Apptimize userId if not already set
    const currentUserId = Apptimize.getCustomerUserId()
    if (currentUserId !== subject) {
      Apptimize.setCustomerUserId(subject)

      // Update feature flags again after setting the user ID
      updateFeatureFlags(setIsFeatureFlagsFetched, isApptimizeInitialized)
    }

    const { _SFDDL: SFDDL } = window

    if (typeof window !== 'undefined') {
      SFDDL.apptimize = {
        subject,
        isVantageScoreMfeEnabled: Apptimize.isFeatureFlagEnabled(
          'isVantageScoreMfeEnabled'
        ),
        isCardStatusTrackerEnabled: Apptimize.isFeatureFlagEnabled(
          'isCardStatusTrackerEnabled'
        ),
        isReplaceYourCardEnabled: Apptimize.isFeatureFlagEnabled(
          'isReplaceYourCardEnabled'
        ),
        isPaymentSecurityEnabled: Apptimize.isFeatureFlagEnabled(
          'isPaymentSecurityEnabled'
        ),
        isDocumentCenterEnabled: Apptimize.isFeatureFlagEnabled(
          'isDocumentCenterEnabled'
        )
      }
    }
  }, [isApptimizeInitialized, subject])

  const isApptimizeEnabled = useMemo(
    () =>
      isApptimizeInitialized
        ? Apptimize.isFeatureFlagEnabled('isApptimizeEnabled')
        : false,
    [isApptimizeInitialized]
  )

  const client = getQueryParam('client') || localStorage.getItem('client')
  const prompt = getQueryParam('prompt')
  const link = getQueryParam('link')
  const session = getQueryParam('session')
  const emailoffer = getQueryParam('emailoffer')
  const sitecodeFromQueryParam = getQueryParam('sitecode')

  if (sitecodeFromQueryParam) {
    sessionStorage.setItem('sitecode', sitecodeFromQueryParam)
  }

  if (emailoffer) {
    sessionStorage.setItem('emailoffer', emailoffer.trim())
  }

  const isUsingMockData = isMockData(config.APP_ENV)
  // TODO: Added useState to avoid race condition. I will remove this useState once Apptimize implemented.
  const [isTokenFetched, setTokenFetched] = useState(false)

  const { data: { accounts = [] } = {}, isError: isCreditAccountsAPIError } =
    useCreditAccounts()
  const allAccountSummaryData = useAllAccountSummary(accounts)?.map(
    (result) => result.data
  )

  const lang = getLanguageFromStorage()

  const { data } = useQuery<DynamicYieldData>(
    ['dynamicYieldData', lang],
    async () => getDynamicYieldResponse()
  )

  const hasCustomCardArtBrick = data?.campaigns?.some((campaign) => {
    return campaign.id === 'customCardArt'
  })

  const ivaUserMetadata = getIvaUserMetadata(allAccountSummaryData)
  const location = useLocation()
  const [showRunningSections, setShowRunningSections] = useState(
    location.pathname !== routes.HOME
  )
  const [isScriptsLoaded, setIsScriptsLoaded] = useState(false)
  const [currentLanguage, setCurrentLanguage] = useState(
    getLanguageFromStorage()
  )

  const handleLanguageChange = () => {
    const newLanguage = currentLanguage === 'en' ? 'es' : 'en'
    localStorage.setItem('language', newLanguage)
    document.documentElement.setAttribute('lang', newLanguage)
    setCurrentLanguage(newLanguage)
    analyticsConfig.defaults.Country_Language = `us|${newLanguage}`
    triggerAnalyticsEvent({ Country_Language: `us|${newLanguage}` })
    window.OneTrust?.changeLanguage()
    triggerQualtrics()
  }

  const authorizedCallback = () => {
    if (isUniversalClient(client) && prompt) {
      const postLogoutRedirectUri = `${window.location.origin}${config.BASENAME}${routes.HOME}?client=universal&prompt=${prompt}&session=cleared`
      handleLogout(postLogoutRedirectUri)
      return
    }

    setTokenFetched(true)
    setShowRunningSections(true)
  }

  const authenticateCallback = async () => {
    if (isUsingMockData) return
    if (client) localStorage.setItem('client', client)

    const isLiquidatedOrNonSupportedClient =
      liquidatedClients.includes(client) || nonSupportedClients.includes(client)

    if (prompt && isUniversalClient(client) && session !== 'cleared') {
      const postLogoutRedirectUri = `${window.location.origin}${config.BASENAME}${routes.HOME}?client=universal&prompt=${prompt}&session=cleared`
      await handleLogout(postLogoutRedirectUri)
      return
    }

    if (
      Object.keys(clientUrls).includes(client) &&
      !isLiquidatedOrNonSupportedClient
    ) {
      let options: AuthenticationRedirectProps | null = null
      if (client === 'tjx' && link === 'rewards') {
        options = {
          navigateTo: JSON.stringify({
            route: routes.REWARDS
          }),
          scope: 'read write'
        }
        sessionStorage.setItem('isTJXDeepLinking', 'true')
      }
      authenticationRedirect(options)
    } else if (isUniversalClient(client)) {
      redirectToUniversalIDP(client, prompt)
    } else {
      redirectToUrl(FIND_ACCOUNT)
    }
  }

  useEffect(() => {
    setLanguageFromQueryParams()
    document.documentElement.setAttribute('lang', currentLanguage)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    updateFeatureFlags(setIsFeatureFlagsFetched, isApptimizeEnabled)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isApptimizeEnabled])

  const setNewRelicCustomAttribute = () => {
    const correlationId = sessionStorage.getItem('correlationId')
    if (correlationId) {
      window?.newrelic?.setCustomAttribute('correlationId', correlationId)
    }
  }

  useEffect(() => {
    if (
      isFeatureFlagsFetched &&
      location.pathname !== routes.LOGOUT &&
      isTokenFetched
    ) {
      initializeOneTrust()
    }
  }, [isFeatureFlagsFetched, location.pathname, isTokenFetched])

  useEffect(() => {
    window.addEventListener('load', setNewRelicCustomAttribute)
    return () => {
      window.removeEventListener('load', setNewRelicCustomAttribute)
    }
  }, [])

  useEffect(() => {
    triggerQualtricsOnLoad(isScriptsLoaded, setIsScriptsLoaded)
  }, [isScriptsLoaded])

  return (
    <Suspense fallback={<Loader />}>
      <ThemeProvider theme={theme}>
        {showRunningSections && <GlobalAppStyles />}
        <AppWrapper data-test="dashboard">
          {showRunningSections && (
            <Header handleLanguageChange={handleLanguageChange} />
          )}
          <AppRoutes
            authenticateCallback={authenticateCallback}
            authorizedCallback={authorizedCallback}
            setTokenFetched={setTokenFetched}
          />
          {showRunningSections && (
            <>
              {location.pathname !== routes.LOGOUT &&
                location.pathname !== routes.ACCOUNT_DETAILS && (
                  <aside>
                    <ActivityModal />
                    {featureFlags.isCreditBalanceRefundDrawerEnabled && (
                      <CreditBalanceRefundDrawer />
                    )}
                    {featureFlags.isCreditLimitIncreaseEnabled && (
                      <CreditLimitIncreaseModal />
                    )}
                    {!featureFlags.isAccountDetailsRouteEnabled && (
                      <DetailsModal />
                    )}
                    <InactivityModal />
                    <MakePaymentModal />
                    {featureFlags.isRewardsDeeplinkEnabled && <RewardsModal />}
                    {featureFlags.isStatementsMfeEnabled &&
                      location.pathname === routes.HOME && <StatementsModal />}
                    {featureFlags.isAddAccountEnabled &&
                      !isCreditAccountsAPIError && <AddAccountModal />}
                    {(featureFlags.isManageCardHolderEnabled ||
                      featureFlags.isManageCardHolderSamsEnabled) && (
                      <ManageCardholderModal />
                    )}
                    {featureFlags.isChangePaymentDueDateEnabled && (
                      <ChangePaymentDueDateModal />
                    )}
                    {featureFlags.isCardStatusTrackerEnabled && (
                      <CardStatusTrackerModal />
                    )}
                    {featureFlags.isCustomCardArtEnabled &&
                      hasCustomCardArtBrick && <CustomCardArtModal />}
                    {featureFlags.isReplaceYourCardEnabled && (
                      <ReplaceYourCardModal />
                    )}
                    {featureFlags.isDigitalCollectionsExperienceEnabled && (
                      <DigitalCollectionsDrawer />
                    )}
                    {featureFlags.isPaymentSecurityEnabled && (
                      <PaymentSecurityModal />
                    )}
                  </aside>
                )}
              <Footer />
            </>
          )}
          {featureFlags.isIvaEnabled && location.pathname !== routes.LOGOUT && (
            <IVAChat
              apiHostName={config.IVA_HOST}
              userMetadata={{
                currentEnterpriseId:
                  selectedAccount || ivaUserMetadata[0]?.enterpriseId || '',
                platformInformation: ivaUserMetadata || []
              }}
              isLoggedIn
              showIvaChatIcon
              platform="VISTA"
            />
          )}
        </AppWrapper>
      </ThemeProvider>
    </Suspense>
  )
}

export default Dashboard
